import { lumi } from '@/lib/lumi'
import { ELEMENT_SITUATIONS, ELEMENT_TRINDADE, ELEMENTS, splitTrindade } from '@/lib/experience'
import type { ElementKey } from '@/lib/experience'
import AdminFormularios from '@/components/AdminFormularios'
import AdminDiamante from '@/components/AdminDiamante'
import AdminAtendimento from '@/components/AdminAtendimento'
import AdminFormulariosCiclo2 from '@/components/AdminFormulariosCiclo2'
import AdminFormulariosCiclo3 from '@/components/AdminFormulariosCiclo3'
import AdminFormulariosCiclo4 from '@/components/AdminFormulariosCiclo4'
import AdminFormulariosCiclo5 from '@/components/AdminFormulariosCiclo5'
import AdminFormulariosCiclo6 from '@/components/AdminFormulariosCiclo6'
import AdminFormulariosMapaCura from '@/components/AdminFormulariosMapaCura'
import { useEffect, useRef, useState } from 'react'

// ─── types ───────────────────────────────────────────────────────────────────

type LeadStatus = 'novo' | 'contatado' | 'em_andamento' | 'concluido'

interface Lead {
  _id: string
  name: string
  email: string
  whatsapp: string
  dominantElement: string
  situationIndex: number
  situationText?: string
  status?: LeadStatus
  observacoes?: string
  createdAt: string
}

// Links de meditação por elemento (espelham AUDIO_META de StepAudio.tsx)
const MEDITATION_LINKS: Record<ElementKey, string> = {
  terra: 'https://www.dropbox.com/scl/fi/b63vz0ujfe4lm7ie4b3pn/Medita-o-Elemento-Terra-M-nica-Maria.mp4?rlkey=y298lmzf0meu3v89r6yb300l8&raw=1',
  agua: 'https://www.dropbox.com/scl/fi/65xcw2nbbg6oc62ik5zot/Medita-o-Elemento-gua-M-nica-Maria.mp4?rlkey=v9y3k4qz9k957vyeevy0381l2&raw=1',
  fogo: 'https://www.dropbox.com/scl/fi/mdszjdc96iec5gczu8hmb/Medita-o-Elemento-Fogo-M-nica-Maria.mp4?rlkey=02uskianbn3s7lnff3m7u60sd&raw=1',
  ar: 'https://www.dropbox.com/scl/fi/ox8br52ogqm65r3tpk7tc/Medita-o-Elemento-Ar-M-nica-Maria.mp4?rlkey=3iaej12123ezbu59788f8cw0t&raw=1',
}

// ─── constants ───────────────────────────────────────────────────────────────

const ADMIN_PASSWORD = 'reative4admin'

const STATUS_OPTIONS: { value: LeadStatus; label: string; color: string; bg: string; border: string }[] = [
  { value: 'novo',         label: 'Novo',          color: '#9ca3af', bg: 'rgba(156,163,175,0.12)', border: 'rgba(156,163,175,0.35)' },
  { value: 'contatado',    label: 'Contatado',     color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.35)'  },
  { value: 'em_andamento', label: 'Em andamento',  color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.35)'  },
  { value: 'concluido',    label: 'Concluído',     color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.35)'   },
]

const ELEMENT_FILTER_OPTIONS = [
  { value: '', label: 'Todos os elementos' },
  { value: 'terra', label: '▽ Terra' },
  { value: 'agua',  label: '▽ Água'  },
  { value: 'fogo',  label: '△ Fogo'  },
  { value: 'ar',    label: '△ Ar'    },
]

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso))
  } catch { return iso }
}

function getSituationText(element: string, index: number, fallback?: string): string {
  const situations = ELEMENT_SITUATIONS[element as ElementKey]
  if (situations && situations[index]) return situations[index]
  return fallback || `Situação ${index + 1}`
}

function getTrindade(element: string, index: number): string {
  const list = ELEMENT_TRINDADE[element as ElementKey]
  if (list && list[index]) return list[index]
  return ''
}

function getMeditationLink(element: string): string {
  return MEDITATION_LINKS[element as ElementKey] ?? ''
}

function buildReadyMessageDay1(lead: Lead): string {
  const elLabel = getElementMeta(lead.dominantElement).label
  const trindade = getTrindade(lead.dominantElement, lead.situationIndex)
  const link = getMeditationLink(lead.dominantElement)
  const parts = splitTrindade(trindade)

  // Monta as três partes com formatação de negrito WhatsApp
  let trindadeFormatada = ''
  if (parts && parts.agradeco && parts.euSou) {
    trindadeFormatada = `*${parts.reconheco}*\n\n*${parts.agradeco}*\n\n*${parts.euSou}*`
  } else {
    trindadeFormatada = `*${trindade}*`
  }

  // Mensagem 1 | Mensagem 2 | Mensagem 3 separadas por linha divisória
  const msg1 = `${lead.name}, aqui é a Mônica Maria. Baseado no que você compartilhou, separei uma prática de liberação para o elemento ${elLabel}.`
  const msg2 = `${trindadeFormatada}\n\nRepita isso todos os dias, de manhã e antes de dormir.`
  const msg3 = `A meditação para fortalecer o Elemento ${elLabel} está aqui: ${link}\n\nEscute hoje à noite com fones de ouvido, no silêncio.`

  return `${msg1}\n\n— — —\n\n${msg2}\n\n— — —\n\n${msg3}`
}

function buildReadyMessageDay2(lead: Lead): string {
  const elLabel = getElementMeta(lead.dominantElement).label

  const msg1 = `Chegou a ler sua Trindade de Cura e a escutar a meditação antes de dormir?`
  const msg2 = `Quero te perguntar uma coisa: você sabe na prática o que acontece em sua vida quando você não desbloqueia o elemento ${elLabel}?`

  return `${msg1}\n\n— — —\n\n${msg2}`
}

function getStatusMeta(status?: LeadStatus) {
  return STATUS_OPTIONS.find(s => s.value === (status || 'novo')) ?? STATUS_OPTIONS[0]
}

// ─── Copy button (reutilizável) ───────────────────────────────────────────────

function CopyButton({
  text,
  label = 'Copiar',
  accent = '#8FAE6F',
  full = false,
}: {
  text: string
  label?: string
  accent?: string
  full?: boolean
}) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // Fallback para ambientes sem clipboard API
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try { document.execCommand('copy') } catch { /* noop */ }
      document.body.removeChild(ta)
    }
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1800)
  }

  return (
    <button
      onClick={handleCopy}
      disabled={!text}
      className={`${full ? 'w-full justify-center' : ''} flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-body tracking-[0.1em] uppercase transition-all duration-150 active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed`}
      style={{ color: copied ? '#22c55e' : accent, background: copied ? 'rgba(34,197,94,0.12)' : `${accent}18`, border: `1px solid ${copied ? 'rgba(34,197,94,0.35)' : `${accent}35`}` }}
      title={label}
      aria-label={label}
    >
      {copied ? (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
      {copied ? 'Copiado' : label}
    </button>
  )
}

// ─── Observações com auto-save ────────────────────────────────────────────────

function ObservacoesField({
  value,
  onSave,
}: {
  value: string
  onSave: (v: string) => void
}) {
  const [text, setText] = useState(value)
  const [saved, setSaved] = useState(false)
  const timerRef = useRef<number | null>(null)

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const v = e.target.value
    setText(v)
    setSaved(false)
    if (timerRef.current) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => {
      onSave(v)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 1800)
    }, 800)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)]">Observações</p>
        {saved && (
          <span className="text-[10px] font-body" style={{ color: '#22c55e' }}>Salvo</span>
        )}
      </div>
      <textarea
        value={text}
        onChange={handleChange}
        rows={3}
        placeholder="Anotações sobre conversas, urgência relatada, follow-ups feitos…"
        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-[13px]
                   text-[var(--ink)] placeholder-[var(--ink-soft)] font-body leading-relaxed
                   focus:outline-none focus:border-white/25 transition-colors resize-y"
      />
    </div>
  )
}

function getElementMeta(key: string) {
  return ELEMENTS[key as ElementKey] ?? { label: key, accent: '#888', accentSoft: '#aaa' }
}

function exportCSV(leads: Lead[]) {
  const header = ['Nome', 'Email', 'WhatsApp', 'Elemento', 'Situação', 'Status', 'Data de entrada']
  const rows = leads.map(l => [
    `"${l.name}"`,
    `"${l.email}"`,
    `"${l.whatsapp}"`,
    `"${getElementMeta(l.dominantElement).label}"`,
    `"${getSituationText(l.dominantElement, l.situationIndex, l.situationText)}"`,
    `"${getStatusMeta(l.status).label}"`,
    `"${formatDate(l.createdAt)}"`,
  ])
  const csv = [header, ...rows].map(r => r.join(',')).join('\n')
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = `leads_4elementos_${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Login screen ─────────────────────────────────────────────────────────────

function AdminLogin({ onAuth }: { onAuth: () => void }) {
  const [pw, setPw]     = useState('')
  const [error, setError] = useState('')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (pw === ADMIN_PASSWORD) { onAuth() }
    else { setError('Senha incorreta. Tente novamente.'); setPw('') }
  }

  return (
    <div className="min-h-screen bg-[#0a0f14] flex flex-col items-center justify-center px-5">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="text-center mb-10">
          <p className="text-[10px] tracking-[0.35em] uppercase text-[var(--ink-soft)] font-body mb-3">
            Reative os <span className="numerals">4</span> Elementos
          </p>
          <h1 className="font-display italic text-[28px] text-[var(--ink)] leading-tight">
            Área Administrativa
          </h1>
          <p className="text-[13px] text-[var(--ink-soft)] mt-2 font-body">
            Digite a senha para acessar
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="password"
              value={pw}
              onChange={e => { setPw(e.target.value); setError('') }}
              placeholder="Senha de acesso"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5
                         text-[var(--ink)] placeholder-[var(--ink-soft)] font-body text-[15px]
                         focus:outline-none focus:border-white/30 transition-colors"
              autoFocus
            />
            {error && (
              <p className="text-red-400 text-[12px] mt-2 font-body">{error}</p>
            )}
          </div>
          <button
            type="submit"
            className="w-full py-3.5 rounded-xl font-body text-[14px] tracking-[0.15em] uppercase
                       transition-all duration-200 active:scale-[0.98]"
            style={{ background: 'rgba(107,142,78,0.25)', color: '#8FAE6F', border: '1px solid rgba(107,142,78,0.4)' }}
          >
            Entrar
          </button>
        </form>
      </div>
    </div>
  )
}

// ─── Status dropdown ──────────────────────────────────────────────────────────

function StatusButton({
  current,
  onChange,
  loading,
}: {
  current?: LeadStatus
  onChange: (s: LeadStatus) => void
  loading: boolean
}) {
  const [open, setOpen] = useState(false)
  const meta = getStatusMeta(current)

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        disabled={loading}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-body
                   tracking-[0.08em] uppercase transition-all duration-150 whitespace-nowrap"
        style={{ color: meta.color, background: meta.bg, border: `1px solid ${meta.border}` }}
      >
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: meta.color }} />
        {meta.label}
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <>
          {/* backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-20 rounded-xl overflow-hidden shadow-2xl
                          border border-white/10 min-w-[150px]"
               style={{ background: '#141c24' }}>
            {STATUS_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false) }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-[12px] font-body
                           hover:bg-white/5 transition-colors text-left"
                style={{ color: opt.color }}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: opt.color }} />
                {opt.label}
                {opt.value === (current || 'novo') && (
                  <svg className="w-3 h-3 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Delete confirm modal ─────────────────────────────────────────────────────

function DeleteConfirmModal({
  name,
  onConfirm,
  onCancel,
}: {
  name: string
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-5"
         style={{ background: 'rgba(10,15,20,0.85)', backdropFilter: 'blur(6px)' }}>
      <div className="w-full max-w-sm rounded-2xl p-6 shadow-2xl border border-white/10"
           style={{ background: '#141c24' }}>
        <div className="flex items-center justify-center w-12 h-12 rounded-full mb-4 mx-auto"
             style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)' }}>
          {/* Trash icon */}
          <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </div>
        <h3 className="font-display italic text-[20px] text-[var(--ink)] text-center mb-2">
          Excluir lead
        </h3>
        <p className="text-[13px] text-[var(--ink-soft)] font-body text-center leading-relaxed mb-6">
          Deseja realmente excluir o lead <span className="text-[var(--ink)] font-medium">{name}</span>?
          Essa ação é permanente e não pode ser desfeita.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 rounded-xl font-body text-[13px] tracking-[0.1em] uppercase
                       transition-all duration-200 active:scale-[0.97]"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--ink-soft)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3 rounded-xl font-body text-[13px] tracking-[0.1em] uppercase
                       transition-all duration-200 active:scale-[0.97]"
            style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' }}
          >
            Excluir
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Lead row (acordeão) ─────────────────────────────────────────────────────

function LeadCard({
  lead,
  onStatusChange,
  onDeleteRequest,
  onObservacoesChange,
  updatingId,
}: {
  lead: Lead
  onStatusChange: (id: string, s: LeadStatus) => void
  onDeleteRequest: (lead: Lead) => void
  onObservacoesChange: (id: string, v: string) => void
  updatingId: string | null
}) {
  const [expanded, setExpanded] = useState(false)

  const el        = getElementMeta(lead.dominantElement)
  const sit       = getSituationText(lead.dominantElement, lead.situationIndex, lead.situationText)
  const trindade  = getTrindade(lead.dominantElement, lead.situationIndex)
  const medLink   = getMeditationLink(lead.dominantElement)
  const readyMsgDay1 = buildReadyMessageDay1(lead)
  const readyMsgDay2 = buildReadyMessageDay2(lead)

  return (
    <div
      className="rounded-2xl border transition-all duration-200"
      style={{
        background: 'rgba(255,255,255,0.03)',
        borderColor: expanded ? `${el.accent}40` : 'rgba(255,255,255,0.08)',
      }}
    >
      {/* ── Cabeçalho sempre visível ── */}
      <div className="p-4 sm:p-5">
        {/* Linha 1: nome + badge + seta + delete */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0 flex-1">
            <p className="font-display italic text-[17px] sm:text-[18px] text-[var(--ink)] leading-snug truncate">
              {lead.name}
            </p>
            <p className="text-[12px] text-[var(--ink-soft)] font-body mt-0.5 truncate">{lead.email}</p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Element badge */}
            <span className="text-[10px] font-body tracking-[0.15em] uppercase px-2 py-1 rounded-lg"
                  style={{ color: el.accent, background: `${el.accent}18`, border: `1px solid ${el.accent}35` }}>
              {el.label}
            </span>

            {/* Expand arrow */}
            <button
              onClick={() => setExpanded(v => !v)}
              className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200
                         hover:bg-white/8 active:scale-90"
              style={{ border: '1px solid rgba(255,255,255,0.08)' }}
              title={expanded ? 'Recolher' : 'Expandir'}
              aria-label={expanded ? 'Recolher detalhes' : 'Expandir detalhes'}
            >
              <svg
                className="w-3.5 h-3.5 transition-transform duration-200"
                style={{ color: 'var(--ink-soft)', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Delete button */}
            <button
              onClick={() => onDeleteRequest(lead)}
              className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150
                         hover:bg-red-500/15 active:scale-90 group"
              style={{ border: '1px solid rgba(239,68,68,0.0)' }}
              title="Excluir lead"
              aria-label="Excluir lead"
            >
              <svg className="w-4 h-4 text-[var(--ink-soft)] group-hover:text-red-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Situação */}
        <p className="text-[12px] sm:text-[13px] font-body leading-snug mb-3 line-clamp-2"
           style={{ color: `${el.accentSoft}cc` }}>
          "{sit}"
        </p>

        {/* Linha 3: whatsapp + data + status */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[12px] text-[var(--ink-soft)] font-body numerals">{lead.whatsapp}</span>
            <span className="text-[11px] text-[var(--ink-soft)] font-body opacity-50 numerals">
              {formatDate(lead.createdAt)}
            </span>
          </div>
          <StatusButton
            current={lead.status}
            onChange={s => onStatusChange(lead._id, s)}
            loading={updatingId === lead._id}
          />
        </div>
      </div>

      {/* ── Detalhes expansíveis ── */}
      {expanded && (
        <div
          className="px-4 sm:px-5 pb-5 space-y-3"
          style={{ borderTop: `1px solid ${el.accent}20` }}
        >
          <div className="pt-4 space-y-3">

            {/* Trindade de Cura */}
            {trindade && (
              <div className="rounded-xl p-3.5" style={{ background: `${el.accent}10`, border: `1px solid ${el.accent}28` }}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-[10px] tracking-[0.2em] uppercase font-body" style={{ color: el.accentSoft }}>
                    Trindade de Cura
                  </p>
                  <CopyButton text={trindade} label="Copiar Trindade" accent={el.accentSoft} />
                </div>
                <p className="text-[12.5px] sm:text-[13px] font-body leading-relaxed text-[var(--ink)] whitespace-pre-line">
                  {trindade}
                </p>
              </div>
            )}

            {/* Link da meditação */}
            {medLink && (
              <div className="rounded-xl p-3.5" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <p className="text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)]">
                    Meditação · {el.label}
                  </p>
                  <CopyButton text={medLink} label="Copiar link" accent={el.accentSoft} />
                </div>
                <a href={medLink} target="_blank" rel="noopener noreferrer"
                   className="text-[12px] font-body break-all hover:underline" style={{ color: el.accentSoft }}>
                  {medLink}
                </a>
              </div>
            )}

            {/* Mensagem Pronta · Dia 1 */}
            <div className="rounded-xl p-3.5" style={{ background: 'rgba(200,146,58,0.1)', border: '1px solid rgba(200,146,58,0.35)' }}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[10px] tracking-[0.2em] uppercase font-body" style={{ color: '#E0B870' }}>
                  ✦ Mensagem Pronta · Dia 1
                </p>
                <CopyButton text={readyMsgDay1} label="Copiar mensagem" accent="#C8923A" />
              </div>
              {readyMsgDay1.split('\n\n— — —\n\n').map((bloco, i) => (
                <div key={i}>
                  {i > 0 && (
                    <div className="flex items-center gap-2 my-2.5">
                      <div className="flex-1 h-px" style={{ background: 'rgba(200,146,58,0.2)' }} />
                      <span className="text-[9px] tracking-[0.25em] uppercase font-body" style={{ color: 'rgba(200,146,58,0.45)' }}>balão {i + 1}</span>
                      <div className="flex-1 h-px" style={{ background: 'rgba(200,146,58,0.2)' }} />
                    </div>
                  )}
                  {i === 0 && (
                    <div className="flex items-center gap-2 mb-2.5">
                      <span className="text-[9px] tracking-[0.25em] uppercase font-body" style={{ color: 'rgba(200,146,58,0.45)' }}>balão 1</span>
                      <div className="flex-1 h-px" style={{ background: 'rgba(200,146,58,0.2)' }} />
                    </div>
                  )}
                  <p className="text-[12.5px] sm:text-[13px] font-body leading-relaxed text-[var(--ink)] whitespace-pre-line opacity-90">
                    {bloco}
                  </p>
                </div>
              ))}
            </div>

            {/* Mensagem Pronta · Dia 2 */}
            <div className="rounded-xl p-3.5" style={{ background: 'rgba(200,146,58,0.06)', border: '1px dashed rgba(200,146,58,0.3)' }}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[10px] tracking-[0.2em] uppercase font-body" style={{ color: '#E0B870' }}>
                  ✦ Mensagem Pronta · Dia 2
                </p>
                <CopyButton text={readyMsgDay2} label="Copiar mensagem" accent="#C8923A" />
              </div>
              {readyMsgDay2.split('\n\n— — —\n\n').map((bloco, i) => (
                <div key={i}>
                  {i > 0 && (
                    <div className="flex items-center gap-2 my-2.5">
                      <div className="flex-1 h-px" style={{ background: 'rgba(200,146,58,0.2)' }} />
                      <span className="text-[9px] tracking-[0.25em] uppercase font-body" style={{ color: 'rgba(200,146,58,0.45)' }}>balão {i + 1}</span>
                      <div className="flex-1 h-px" style={{ background: 'rgba(200,146,58,0.2)' }} />
                    </div>
                  )}
                  {i === 0 && (
                    <div className="flex items-center gap-2 mb-2.5">
                      <span className="text-[9px] tracking-[0.25em] uppercase font-body" style={{ color: 'rgba(200,146,58,0.45)' }}>balão 1</span>
                      <div className="flex-1 h-px" style={{ background: 'rgba(200,146,58,0.2)' }} />
                    </div>
                  )}
                  <p className="text-[12.5px] sm:text-[13px] font-body leading-relaxed text-[var(--ink)] whitespace-pre-line opacity-90">
                    {bloco}
                  </p>
                </div>
              ))}
            </div>

            {/* Observações */}
            <ObservacoesField
              value={lead.observacoes || ''}
              onSave={v => onObservacoesChange(lead._id, v)}
            />

          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main Admin page ──────────────────────────────────────────────────────────

export default function PageAdmin() {
  const [authed, setAuthed]           = useState(() => sessionStorage.getItem('r4e_admin') === '1')
  const [tab, setTab]                 = useState<'leads' | 'mapa' | 'diamante' | 'atendimento' | 'formularios' | 'ciclo2' | 'ciclo3' | 'ciclo4' | 'ciclo5' | 'ciclo6'>('leads')
  const [leads, setLeads]             = useState<Lead[]>([])
  const [loading, setLoading]         = useState(false)
  const [elementFilter, setElementFilter] = useState('')
  const [updatingId, setUpdatingId]   = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Lead | null>(null)

  // ── fetch ──
  useEffect(() => {
    if (!authed) return
    if (tab !== 'leads') return
    fetchLeads()
  }, [authed, tab])

  async function fetchLeads() {
    setLoading(true)
    try {
      const filter = elementFilter ? { dominantElement: elementFilter } : {}
      const { list } = await lumi.entities.leads_4elementos.list({
        filter,
        sort: { createdAt: -1 },
        limit: 500,
      })
      setLeads(list as Lead[])
    } catch (e) {
      console.error('Erro ao buscar leads:', e)
    } finally {
      setLoading(false)
    }
  }

  // Re-fetch when filter changes
  useEffect(() => {
    if (!authed) return
    if (tab !== 'leads') return
    fetchLeads()
  }, [elementFilter])

  // ── status update ──
  async function handleStatusChange(id: string, status: LeadStatus) {
    setUpdatingId(id)
    // Optimistic update
    setLeads(prev => prev.map(l => l._id === id ? { ...l, status } : l))
    try {
      await lumi.entities.leads_4elementos.update(id, {
        status,
        updatedAt: new Date().toISOString(),
      })
    } catch (e) {
      console.error('Erro ao atualizar status:', e)
      // Revert on failure
      fetchLeads()
    } finally {
      setUpdatingId(null)
    }
  }

  // ── observações (auto-save) ──
  async function handleObservacoesChange(id: string, observacoes: string) {
    setLeads(prev => prev.map(l => l._id === id ? { ...l, observacoes } : l))
    try {
      await lumi.entities.leads_4elementos.update(id, {
        observacoes,
        updatedAt: new Date().toISOString(),
      })
    } catch (e) {
      console.error('Erro ao salvar observações:', e)
    }
  }

  // ── delete ──
  async function handleDeleteConfirm() {
    if (!deleteTarget) return
    const id = deleteTarget._id
    setDeleteTarget(null)
    setLeads(prev => prev.filter(l => l._id !== id))
    try {
      await lumi.entities.leads_4elementos.delete(id)
    } catch (e) {
      console.error('Erro ao excluir lead:', e)
      fetchLeads()
    }
  }

  // ── auth gate ──
  if (!authed) {
    return (
      <AdminLogin onAuth={() => {
        sessionStorage.setItem('r4e_admin', '1')
        setAuthed(true)
      }} />
    )
  }

  // ── stats ──
  const total   = leads.length
  const byEl    = (k: string) => leads.filter(l => l.dominantElement === k).length
  const byStatus = (s: LeadStatus) => leads.filter(l => (l.status || 'novo') === s).length

  return (
    <div className="min-h-screen bg-[#0a0f14]">
      {/* ── Header ── */}
      <header className="border-b border-white/8 px-4 sm:px-6 md:px-10 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div>
            <p className="text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-[var(--ink-soft)] font-body">
              Reative os <span className="numerals">4</span> Elementos
            </p>
            <h1 className="font-display italic text-[22px] sm:text-[26px] text-[var(--ink)] leading-tight">
              {tab === 'leads' ? 'Painel de Leads' : tab === 'mapa' ? 'Mapa de Cura dos 4 Elementos' : tab === 'formularios' ? 'Formulário Ciclo 1' : tab === 'diamante' ? 'Reacenda o Diamante' : tab === 'ciclo2' ? 'Formulário Ciclo 2' : tab === 'ciclo3' ? 'Formulário Ciclo 3' : tab === 'ciclo4' ? 'Formulário Ciclo 4' : tab === 'ciclo5' ? 'Formulário Ciclo 5' : tab === 'ciclo6' ? 'Formulário Ciclo 6' : 'Triagem de Atendimento'}
            </h1>
          </div>
          <button
            onClick={() => {
              sessionStorage.removeItem('r4e_admin')
              setAuthed(false)
            }}
            className="text-[11px] tracking-[0.15em] uppercase font-body text-[var(--ink-soft)]
                       hover:text-[var(--ink)] transition-colors px-3 py-2 rounded-lg hover:bg-white/5"
          >
            Sair
          </button>
        </div>

        {/* Tabs */}
        <div className="max-w-6xl mx-auto mt-4 flex flex-wrap gap-1 p-1 rounded-xl w-fit"
             style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {[
            { k: 'leads',        label: 'Leads' },
            { k: 'mapa',         label: 'Mapa de Cura dos 4 Elementos' },
            { k: 'diamante',     label: 'Diamante' },
            { k: 'atendimento',  label: 'Triagem' },
            { k: 'formularios',  label: 'Ciclo 1' },
            { k: 'ciclo2',       label: 'Ciclo 2' },
            { k: 'ciclo3',       label: 'Ciclo 3' },
            { k: 'ciclo4',       label: 'Ciclo 4' },
            { k: 'ciclo5',       label: 'Ciclo 5' },
            { k: 'ciclo6',       label: 'Ciclo 6' },
          ].map(opt => (
            <button
              key={opt.k}
              onClick={() => setTab(opt.k as typeof tab)}
              className="px-4 py-2 rounded-lg text-[12px] tracking-[0.15em] uppercase font-body transition-all"
              style={
                tab === opt.k
                  ? { background: 'rgba(107,142,78,0.2)', color: '#8FAE6F', border: '1px solid rgba(107,142,78,0.4)' }
                  : { color: 'var(--ink-soft)' }
              }
            >
              {opt.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-8">

        {tab === 'mapa' ? (
          <AdminFormulariosMapaCura />
        ) : tab === 'formularios' ? (
          <AdminFormularios />
        ) : tab === 'diamante' ? (
          <AdminDiamante />
        ) : tab === 'atendimento' ? (
          <AdminAtendimento />
        ) : tab === 'ciclo2' ? (
          <AdminFormulariosCiclo2 />
        ) : tab === 'ciclo3' ? (
          <AdminFormulariosCiclo3 />
        ) : tab === 'ciclo4' ? (
          <AdminFormulariosCiclo4 />
        ) : tab === 'ciclo5' ? (
          <AdminFormulariosCiclo5 />
        ) : tab === 'ciclo6' ? (
          <AdminFormulariosCiclo6 />
        ) : (
        <>

        {/* ── Stats bar ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total',       value: total,               color: '#fff'    },
            { label: 'Terra',       value: byEl('terra'),       color: '#8FAE6F' },
            { label: 'Água',        value: byEl('agua'),        color: '#5FA0D9' },
            { label: 'Fogo',        value: byEl('fogo'),        color: '#F09A5E' },
          ].map(s => (
            <div key={s.label} className="rounded-xl p-3 sm:p-4 border border-white/8"
                 style={{ background: 'rgba(255,255,255,0.025)' }}>
              <p className="text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)] mb-1">{s.label}</p>
              <p className="numerals text-[26px] sm:text-[30px] font-display leading-none" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* ── Status summary ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {STATUS_OPTIONS.map(s => (
            <div key={s.value} className="rounded-xl p-3 border"
                 style={{ background: s.bg, borderColor: s.border }}>
              <p className="text-[10px] tracking-[0.2em] uppercase font-body mb-1" style={{ color: s.color }}>{s.label}</p>
              <p className="numerals text-[22px] font-display leading-none" style={{ color: s.color }}>{byStatus(s.value)}</p>
            </div>
          ))}
        </div>

        {/* ── Toolbar: filter + export ── */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          {/* Element filter */}
          <div className="relative">
            <select
              value={elementFilter}
              onChange={e => setElementFilter(e.target.value)}
              className="appearance-none bg-white/5 border border-white/10 rounded-xl
                         pl-3 pr-8 py-2.5 text-[13px] font-body text-[var(--ink)]
                         focus:outline-none focus:border-white/25 transition-colors cursor-pointer"
            >
              {ELEMENT_FILTER_OPTIONS.map(o => (
                <option key={o.value} value={o.value} style={{ background: '#141c24' }}>{o.label}</option>
              ))}
            </select>
            <svg className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--ink-soft)] pointer-events-none"
                 fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Ar stat (não cabe nos 4 stats) */}
          <div className="flex items-center gap-1.5 text-[12px] font-body text-[var(--ink-soft)]">
            <span className="w-2 h-2 rounded-full" style={{ background: '#D4C3EA' }} />
            <span>Ar: <span className="numerals" style={{ color: '#D4C3EA' }}>{byEl('ar')}</span></span>
          </div>

          {/* Export CSV */}
          <button
            onClick={() => exportCSV(leads)}
            disabled={leads.length === 0}
            className="ml-auto flex items-center gap-2 px-4 py-2.5 rounded-xl font-body text-[13px]
                       tracking-[0.1em] uppercase transition-all duration-200 active:scale-[0.97]
                       disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: 'rgba(107,142,78,0.15)', color: '#8FAE6F', border: '1px solid rgba(107,142,78,0.3)' }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Exportar CSV
          </button>
        </div>

        {/* ── Lead list ── */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 rounded-full border-2 border-[#8FAE6F] border-t-transparent animate-spin" />
              <p className="text-[13px] font-body text-[var(--ink-soft)]">Carregando leads…</p>
            </div>
          </div>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <p className="text-[15px] font-display italic text-[var(--ink-soft)] mb-2">Nenhum lead encontrado</p>
            <p className="text-[12px] font-body text-[var(--ink-soft)] opacity-60">
              {elementFilter ? 'Tente remover o filtro de elemento.' : 'Os leads aparecerão aqui após o preenchimento da experiência.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {leads.map(lead => (
              <LeadCard
                key={lead._id}
                lead={lead}
                onStatusChange={handleStatusChange}
                onDeleteRequest={setDeleteTarget}
                onObservacoesChange={handleObservacoesChange}
                updatingId={updatingId}
              />
            ))}
          </div>
        )}

        {/* total count */}
        {!loading && leads.length > 0 && (
          <p className="text-center text-[11px] font-body text-[var(--ink-soft)] opacity-50 mt-6 numerals">
            {leads.length} {leads.length === 1 ? 'lead' : 'leads'} exibido{leads.length === 1 ? '' : 's'}
          </p>
        )}
        </>
        )}
      </main>

      {/* ── Delete modal ── */}
      {deleteTarget && (
        <DeleteConfirmModal
          name={deleteTarget.name}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  )
}
